import { useMemo } from 'react';

import useGetCompanyNumbers from './queries/useGetCompanyNumbers';
import useGetCompanyUsers from './queries/useGetCompanyUsers';
import useGetRecentCallRecords from './queries/useGetRecentCallRecords';
import useGetAvailableServiceProfiles from './queries/useGetAvailableServiceProfiles';
import useGetAvailableNumberBlocks from './queries/useGetAvailableNumberBlocks';
import ScopeType from 'utils/enums/ScopeType';
import { resolveSetupProgress } from './setupSteps';
import resolveStepServices from './resolveStepServices';

/**
 * (3145) Gathers everything the setup progress bar needs for one company.
 *
 * Services come from the dashboard payload the page already polls (3128), so
 * only numbers, users, call records, public pool blocks and the Add Services
 * catalogue are fetched here.
 *
 * @param {object} params
 * @param {string} params.companyId
 * @param {object[]|null} params.services - dashboard services; null while unknown
 * @param {object[]} params.appAuths - dashboard app auths
 * @param {number} params.currentScope - the header's view scope
 * @param {boolean} [params.companyApproved] - from /company/{id}; undefined while loading
 */
const useSetupProgress = ({
    companyId,
    services,
    appAuths,
    currentScope,
    companyApproved,
}) => {
    const numbersQuery = useGetCompanyNumbers(companyId);
    const usersQuery = useGetCompanyUsers(companyId);
    const callRecordsQuery = useGetRecentCallRecords(companyId);
    const serviceProfilesQuery = useGetAvailableServiceProfiles(companyId);
    const numberBlocksQuery = useGetAvailableNumberBlocks(companyId);

    // 3128: the dashboard counts app auths as cards too.
    const hasServices = (services?.length || 0) + (appAuths?.length || 0) > 0;

    // 3154: the same conditions the Numbers page uses to offer its Available
    // Number Blocks drawer, so "Go to Numbers" never lands on a page
    // that won't open it.
    const hasPublicPoolNumbers =
        currentScope !== ScopeType.PARTNER &&
        currentScope !== ScopeType.WHOLESALER &&
        companyApproved !== false &&
        (numberBlocksQuery.data?.length ?? 0) > 0;

    const progress = useMemo(
        () =>
            resolveSetupProgress({
                hasServices,
                numbers: numbersQuery.data,
                users: usersQuery.data,
                hasCallRecords: callRecordsQuery.data,
                hasPublicPoolNumbers,
            }),
        [
            hasServices,
            numbersQuery.data,
            usersQuery.data,
            callRecordsQuery.data,
            hasPublicPoolNumbers,
        ],
    );

    const stepServices = useMemo(
        () => resolveStepServices(services, serviceProfilesQuery.data),
        [services, serviceProfilesQuery.data],
    );

    // Stay unresolved until every step is known, so the bar never flashes up
    // for a company that turns out to be fully set up.
    const isLoading =
        (services === null && !appAuths?.length) ||
        numbersQuery.isLoading ||
        usersQuery.isLoading ||
        callRecordsQuery.isLoading ||
        numberBlocksQuery.isLoading;

    return { ...progress, stepServices, isLoading };
};

export default useSetupProgress;
